import { useMemo, useState } from "react";
import { MediaField } from "@/components/admin/MediaField";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RecipeEditor } from "@/components/admin/RecipeEditor";
import {
  listBeverageCategories,
  listBeveragesAdmin,
  upsertBeverage,
  upsertBeverageCategory,
  deleteBeverage,
  deleteBeverageCategory,
  getBeverageCompletenessSummary,
  type BeverageAdminRow,
  type BeverageCategoryRow,
} from "@/lib/admin/beverages.functions";
import {
  SERVING_UNITS,
  SERVING_TEMPERATURES,
  AVAILABILITY_STATUSES,
  type BeverageInput,
  type BeverageCategoryInput,
} from "@/lib/admin/beverages.schemas";

export const Route = createFileRoute("/_admin/admin/beverages")({
  head: () => ({ meta: [{ title: "Beverages · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminBeveragesPage,
});

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function AdminBeveragesPage() {
  const listFn = useServerFn(listBeveragesAdmin);
  const catFn = useServerFn(listBeverageCategories);
  const summaryFn = useServerFn(getBeverageCompletenessSummary);
  const qc = useQueryClient();

  const bevQ = useQuery({ queryKey: ["admin", "beverages"], queryFn: () => listFn({}) });
  const catsQ = useQuery({ queryKey: ["admin", "beverage-categories"], queryFn: () => catFn({}) });
  const sumQ = useQuery({ queryKey: ["admin", "beverage-summary"], queryFn: () => summaryFn({}) });

  const beverages = (bevQ.data as { items: BeverageAdminRow[] } | undefined)?.items ?? [];
  const categories = (catsQ.data as { items: BeverageCategoryRow[] } | undefined)?.items ?? [];
  const summary = sumQ.data as
    | { total: number; complete: number; partial: number; incomplete: number; avg: number; worst: BeverageAdminRow[] }
    | undefined;

  const [search, setSearch] = useState("");
  const [openBev, setOpenBev] = useState<BeverageAdminRow | null | "new">(null);
  const [openCat, setOpenCat] = useState<BeverageCategoryRow | null | "new">(null);
  const [confirmDelBev, setConfirmDelBev] = useState<BeverageAdminRow | null>(null);
  const [confirmDelCat, setConfirmDelCat] = useState<BeverageCategoryRow | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return beverages;
    return beverages.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.slug.toLowerCase().includes(q) ||
        (b.description ?? "").toLowerCase().includes(q),
    );
  }, [beverages, search]);

  const upsertBevFn = useServerFn(upsertBeverage);
  const delBevFn = useServerFn(deleteBeverage);
  const upsertCatFn = useServerFn(upsertBeverageCategory);
  const delCatFn = useServerFn(deleteBeverageCategory);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["admin", "beverages"] });
    qc.invalidateQueries({ queryKey: ["admin", "beverage-categories"] });
    qc.invalidateQueries({ queryKey: ["admin", "beverage-summary"] });
  };

  const saveBev = useMutation({
    mutationFn: async (v: { id?: string; input: BeverageInput }) => upsertBevFn({ data: v }),
    onSuccess: () => {
      toast.success("Beverage saved");
      setOpenBev(null);
      refreshAll();
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });
  const delBev = useMutation({
    mutationFn: async (id: string) => delBevFn({ data: { id } }),
    onSuccess: () => { toast.success("Beverage deleted"); refreshAll(); },
    onError: (e: Error) => toast.error(`Delete failed: ${e.message}`),
  });
  const saveCat = useMutation({
    mutationFn: async (v: { id?: string; input: BeverageCategoryInput }) => upsertCatFn({ data: v }),
    onSuccess: () => { toast.success("Category saved"); setOpenCat(null); refreshAll(); },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });
  const delCat = useMutation({
    mutationFn: async (id: string) => delCatFn({ data: { id } }),
    onSuccess: () => { toast.success("Category deleted"); refreshAll(); },
    onError: (e: Error) => toast.error(`Delete failed: ${e.message}`),
  });

  return (
    <div>
      <AdminPageHeader
        title="Beverages"
        description="Manage the beverage library, categories and completeness. Shares the cart with the à-la-carte food catalogue."
        actions={
          <AdminDataToolbar
            exportName="beverages"
            moduleId="beverages"
            onImported={refreshAll}
            rows={() =>
              filtered.map((b) => ({
                Slug: b.slug,
                Name: b.name,
                Description: b.description ?? "",
                Veg: b.is_veg ? "Yes" : "No",
                Active: b.is_active ? "Yes" : "No",
              }))
            }
          />
        }
      />


      <Tabs defaultValue="library" className="mt-4">
        <TabsList>
          <TabsTrigger value="library">Library</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="completeness">Completeness</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              placeholder="Search beverages…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={() => setOpenBev("new")}>
              <Plus className="mr-2 h-4 w-4" /> New beverage
            </Button>
          </div>

          {bevQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <Alert><AlertDescription>No beverages match.</AlertDescription></Alert>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Serving</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Price</th>
                    <th className="px-3 py-2">Complete</th>
                    <th className="px-3 py-2">Public</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => {
                    const cat = categories.find((c) => c.id === b.category_id);
                    return (
                      <tr key={b.id} className="border-t">
                        <td className="px-3 py-2">
                          <div className="font-medium">{b.name}</div>
                          <div className="text-xs text-muted-foreground">{b.slug}</div>
                        </td>
                        <td className="px-3 py-2">{cat?.name ?? "—"}</td>
                        <td className="px-3 py-2">
                          {[b.size_label, b.serving_unit, b.serving_temperature].filter(Boolean).join(" · ") || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={b.availability_status === "in_stock" ? "default" : "secondary"}>
                            {b.availability_status.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          {b.current_price_paise != null ? `₹${(b.current_price_paise / 100).toFixed(0)}` : <span className="text-destructive">unset</span>}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{b.completeness_score ?? 0}%</td>
                        <td className="px-3 py-2">
                          {(() => {
                            // Exact reason an Active item is not on the website.
                            const reason = !b.is_active
                              ? "Switched off"
                              : !b.is_public
                                ? "Hidden from website"
                                : !b.alacarte_active
                                  ? "À la carte off"
                                  : cat && !cat.is_active
                                    ? `Category "${cat.name}" is hidden`
                                    : b.availability_status === "out_of_stock" || b.availability_status === "seasonal_unavailable"
                                      ? b.availability_status.replace(/_/g, " ")
                                      : b.current_price_paise == null
                                        ? "No live price"
                                        : null;
                            return reason ? (
                              <div className="space-y-1">
                                <Badge variant="outline">Not on website</Badge>
                                <div className="text-xs text-destructive">{reason}</div>
                              </div>
                            ) : (
                              <Badge>Live</Badge>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => setOpenBev(b)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmDelBev(b)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setOpenCat("new")}>
              <Plus className="mr-2 h-4 w-4" /> New category
            </Button>
          </div>
          <div className="rounded-md border">
            {catsQ.isLoading ? (
              <div className="p-3 text-sm text-muted-foreground">Loading…</div>
            ) : categories.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">No categories yet.</div>
            ) : (
              <ul className="divide-y">
                {categories.map((c) => (
                  <li key={c.id} className="flex items-center justify-between p-3">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.slug}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Hidden</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => setOpenCat(c)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelCat(c)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="completeness" className="space-y-4">
          {summary ? (
            <div className="grid gap-3 md:grid-cols-4">
              <StatCard label="Total beverages" value={summary.total} />
              <StatCard label="Fully complete" value={summary.complete} />
              <StatCard label="Partial (60-99%)" value={summary.partial} />
              <StatCard label="Avg completeness" value={`${summary.avg}%`} />
            </div>
          ) : null}
          {summary && summary.worst.length > 0 ? (
            <div className="rounded-md border">
              <div className="border-b bg-muted/40 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
                Needs attention
              </div>
              <ul className="divide-y">
                {summary.worst.map((b) => (
                  <li key={b.id} className="flex items-center justify-between p-3">
                    <div>
                      <div className="font-medium">{b.name}</div>
                      <div className="text-xs text-muted-foreground">{b.slug}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="tabular-nums text-sm">{b.completeness_score ?? 0}%</span>
                      <Button size="sm" variant="outline" onClick={() => setOpenBev(b)}>Edit</Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>

      {openBev ? (
        <BeverageDialog
          existing={openBev === "new" ? null : openBev}
          categories={categories}
          onClose={() => setOpenBev(null)}
          onSave={(v) => saveBev.mutate(v)}
          saving={saveBev.isPending}
        />
      ) : null}

      {openCat ? (
        <CategoryDialog
          existing={openCat === "new" ? null : openCat}
          onClose={() => setOpenCat(null)}
          onSave={(v) => saveCat.mutate(v)}
          saving={saveCat.isPending}
        />
      ) : null}

      <AlertDialog open={confirmDelBev !== null} onOpenChange={(v) => !v && setConfirmDelBev(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this beverage?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes "{confirmDelBev?.name}" permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmDelBev) { delBev.mutate(confirmDelBev.id); setConfirmDelBev(null); } }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelCat !== null} onOpenChange={(v) => !v && setConfirmDelCat(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this category?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes "{confirmDelCat?.name}" permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmDelCat) { delCat.mutate(confirmDelCat.id); setConfirmDelCat(null); } }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function BeverageDialog({
  existing,
  categories,
  onClose,
  onSave,
  saving,
}: {
  existing: BeverageAdminRow | null;
  categories: BeverageCategoryRow[];
  onClose: () => void;
  onSave: (v: { id?: string; input: BeverageInput }) => void;
  saving: boolean;
}) {
  const [f, setF] = useState<BeverageInput>(() => ({
    slug: existing?.slug ?? "",
    name: existing?.name ?? "",
    description: existing?.description ?? null,
    category_id: existing?.category_id ?? null,
    image_url: existing?.image_url ?? null,
    alt_text: existing?.alt_text ?? null,
    size_label: existing?.size_label ?? null,
    serving_unit: (existing?.serving_unit as BeverageInput["serving_unit"]) ?? null,
    serving_temperature: (existing?.serving_temperature as BeverageInput["serving_temperature"]) ?? null,
    availability_status: (existing?.availability_status as BeverageInput["availability_status"]) ?? "in_stock",
    calories_kcal: existing?.calories_kcal ?? null,
    protein_g: existing?.protein_g ?? null,
    carbs_g: existing?.carbs_g ?? null,
    fat_g: existing?.fat_g ?? null,
    sugar_g: existing?.sugar_g ?? null,
    ingredients: existing?.ingredients ?? [],
    allergens: existing?.allergens ?? [],
    is_veg: existing?.is_veg ?? true,
    is_vegan: existing?.is_vegan ?? false,
    alacarte_active: existing?.alacarte_active ?? true,
    is_public: existing?.is_public ?? true,
    is_active: existing?.is_active ?? true,
    sort_order: existing?.sort_order ?? 0,
    available_days: existing?.available_days ?? [0, 1, 2, 3, 4, 5, 6],
    price_paise: existing?.current_price_paise ?? null,
    recommendation_tags: existing?.recommendation_tags ?? [],
    recommended_dish_ids: existing?.recommended_dish_ids ?? [],
  }));

  const toggleDay = (d: number) => {
    setF((s) => ({
      ...s,
      available_days: s.available_days.includes(d)
        ? s.available_days.filter((x) => x !== d)
        : [...s.available_days, d].sort(),
    }));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ id: existing?.id, input: f });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[95vw] overflow-y-auto sm:max-w-[90vw] xl:max-w-[1500px]">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit beverage" : "New beverage"}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
          </TabsList>
          <TabsContent value="ingredients" className="pt-4">
            <RecipeEditor kind="beverage" itemId={existing?.id ?? null} itemTitle={f.name} />
          </TabsContent>
          <TabsContent value="details">
        <form onSubmit={submit} className="space-y-4">

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Name">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
            </Field>
            <Field label="Slug">
              <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required />
            </Field>
            <Field label="Category">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.category_id ?? ""}
                onChange={(e) => setF({ ...f, category_id: e.target.value || null })}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Price (₹)">
              <Input
                type="number"
                min="0"
                value={f.price_paise == null ? "" : String(f.price_paise / 100)}
                onChange={(e) =>
                  setF({ ...f, price_paise: e.target.value === "" ? null : Math.round(Number(e.target.value) * 100) })
                }
                placeholder="e.g. 120"
              />
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={3} value={f.description ?? ""} onChange={(e) => setF({ ...f, description: e.target.value || null })} />
          </Field>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Image URL (https)">
              <MediaField value={f.image_url ?? ""} onChange={(next) => setF({ ...f, image_url: next || null })} />
            </Field>
            <Field label="Alt text">
              <Input value={f.alt_text ?? ""} onChange={(e) => setF({ ...f, alt_text: e.target.value || null })} />
            </Field>
            <Field label="Size label">
              <Input value={f.size_label ?? ""} onChange={(e) => setF({ ...f, size_label: e.target.value || null })} placeholder="e.g. 250 ml" />
            </Field>
            <Field label="Serving unit">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.serving_unit ?? ""}
                onChange={(e) => setF({ ...f, serving_unit: (e.target.value || null) as BeverageInput["serving_unit"] })}
              >
                <option value="">—</option>
                {SERVING_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Serving temperature">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.serving_temperature ?? ""}
                onChange={(e) => setF({ ...f, serving_temperature: (e.target.value || null) as BeverageInput["serving_temperature"] })}
              >
                <option value="">—</option>
                {SERVING_TEMPERATURES.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Availability">
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.availability_status}
                onChange={(e) => setF({ ...f, availability_status: e.target.value as BeverageInput["availability_status"] })}
              >
                {AVAILABILITY_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-3 md:grid-cols-5">
            {(["calories_kcal","protein_g","carbs_g","fat_g","sugar_g"] as const).map((k) => (
              <Field key={k} label={k.replace(/_/g," ")}>
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={f[k] == null ? "" : String(f[k])}
                  onChange={(e) => setF({ ...f, [k]: e.target.value === "" ? null : Number(e.target.value) } as BeverageInput)}
                />
              </Field>
            ))}
          </div>

          <Field label="Available days of the week">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm"
                onClick={() => setF({ ...f, available_days: [0,1,2,3,4,5,6] })}>All days</Button>
              {DAYS.map((d, i) => {
                const on = f.available_days.includes(i);
                return (
                  <Button key={i} type="button" size="sm" variant={on ? "default" : "outline"} onClick={() => toggleDay(i)}>
                    {d}
                  </Button>
                );
              })}
            </div>
          </Field>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Ingredients (comma separated)">
              <Input
                value={(f.ingredients ?? []).join(", ")}
                onChange={(e) => setF({ ...f, ingredients: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              />
            </Field>
            <Field label="Allergens (comma separated)">
              <Input
                value={(f.allergens ?? []).join(", ")}
                onChange={(e) => setF({ ...f, allergens: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              />
            </Field>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <ToggleRow label="Veg" checked={f.is_veg} onChange={(v) => setF({ ...f, is_veg: v })} />
            <ToggleRow label="Vegan" checked={f.is_vegan} onChange={(v) => setF({ ...f, is_vegan: v })} />
            <ToggleRow label="Public" checked={f.is_public} onChange={(v) => setF({ ...f, is_public: v })} />
            <ToggleRow label="Active" checked={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
            <ToggleRow label="À la carte" checked={f.alacarte_active} onChange={(v) => setF({ ...f, alacarte_active: v })} />
            <Field label="Sort order">
              <Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} />
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
          </TabsContent>
        </Tabs>

      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({
  existing,
  onClose,
  onSave,
  saving,
}: {
  existing: BeverageCategoryRow | null;
  onClose: () => void;
  onSave: (v: { id?: string; input: BeverageCategoryInput }) => void;
  saving: boolean;
}) {
  const [f, setF] = useState<BeverageCategoryInput>(() => ({
    slug: existing?.slug ?? "",
    name: existing?.name ?? "",
    description: existing?.description ?? null,
    sort_order: existing?.sort_order ?? 0,
    is_active: existing?.is_active ?? true,
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ id: existing?.id, input: f });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{existing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <Field label="Slug"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required /></Field>
          <Field label="Description"><Textarea rows={2} value={f.description ?? ""} onChange={(e) => setF({ ...f, description: e.target.value || null })} /></Field>
          <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} /></Field>
          <ToggleRow label="Active" checked={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}